import React, { useState } from "react";
import { css } from "@emotion/css";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";
import { newLogo } from "../assets/images";

const { FiMail, FiPhone, FiMenu, FiX } = FiIcons;

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const headerStyles = {
        header: css`
            background-color: white;
            box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
        `,
        container: css`
            margin: 0;
            padding: 0 1rem;
            @media (min-width: 640px) {
                padding: 0 1.5rem;
            }
            @media (min-width: 1024px) {
                padding: 1rem 2rem;
            }
        `,
        headerContent: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.2rem 0;
            @media (max-width: 1040px) {
                padding: 1rem 0;
            }
        `,
        logoSection: css`
            display: flex;
            align-items: center;
            gap: 0.75rem;
        `,
        logo: css`
            width: 7rem;
            height: 7rem;
            border-radius: 9999px;
            object-fit: cover;
            
            @media (max-width: 768px) {
                width: 3.5rem;
                height: 3.5rem;
            }
        `,
        title: css`
            font-size: 35px;
            font-weight: 900;
            color: #f5b940;
            font-family: "Akagi Pro", sans-serif;

            @media (max-width: 768px) {
                font-size: 24px;
            }
        `,
        desktopMenu: css`
            display: none;
            align-items: center;
            gap: 1.5rem;
            @media (min-width: 768px) {
                display: flex;
            }
            @media (min-width: 768px) and (max-width: 1040px) {
                display: flex;
                flex-direction: column;
                align-items: flex-end;
                row-gap: 10px;
            }
        `,
        contactItem: css`
            display: flex;
            align-items: center;
            gap: 0.5rem;
            color: #4b5563;
            border: 1px solid #f0f0f0;
            padding: 7px;
            border-radius: 15px;
        `,
        contactText: css`
            font-size: 0.875rem;
        `,
        registerButton: css`
            background-color: #2563eb;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.5rem;
            transition: background-color 0.2s;
            &:hover {
                background-color: #1d4ed8;
            }
        `,
        mobileMenuButton: css`
            display: block;
            @media (min-width: 768px) {
                display: none;
            }
        `,
        mobileMenu: css`
            display: block;
            padding: 1rem 0;
            border-top: 1px solid #e5e7eb;
            @media (min-width: 768px) {
                display: none;
            }
        `,
        mobileMenuContent: css`
            display: flex;
            flex-direction: column;
            gap: 1rem;
        `,
        mobileContactItem: css`
            display: flex;
            align-items: center;
            gap: 0.5rem;
            color: #4b5563;
        `,
        mobileRegisterButton: css`
            width: 100%;
            background-color: #2563eb;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.5rem;
            transition: background-color 0.2s;
            &:hover {
                background-color: #1d4ed8;
            }
        `,
        icon: css`
            width: 1rem;
            height: 1rem;
        `,
        menuIcon: css`
            background: white;
            width: 1.5rem;
            height: 1.5rem;
        `,
    };

    const handleMenuToggle = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleMenuToggle();
        }
    };

    return (
        <header className={headerStyles.header} role="banner">
            <div className={headerStyles.container}>
                <div className={headerStyles.headerContent}>
                    <div className={headerStyles.logoSection}>
                        <img
                            src={newLogo}
                            alt="Club Mango Logo - A distinctive logo representing the Club Mango community"
                            className={headerStyles.logo}
                        />
                        <h1 className={headerStyles.title}>
                            Club Mango Friends
                        </h1>
                    </div>

                    <nav
                        className={headerStyles.desktopMenu}
                        role="navigation"
                        aria-label="Main navigation"
                    >
                        <div
                            className={headerStyles.contactItem}
                            role="group"
                            aria-label="Contact information"
                        >
                            <SafeIcon
                                icon={FiMail}
                                className={headerStyles.icon}
                                aria-hidden="true"
                            />
                            <span className={headerStyles.contactText}>
                                <a
                                    href="mailto:clubmangofriends@divineharmony.co.in"
                                    aria-label="Send email to clubmangofriends@divineharmony.co.in"
                                >
                                    clubmangofriends@divineharmony.co.in
                                </a>
                            </span>
                        </div>
                        <div
                            className={headerStyles.contactItem}
                            role="group"
                            aria-label="Phone contact"
                        >
                            <SafeIcon
                                icon={FiPhone}
                                className={headerStyles.icon}
                                aria-hidden="true"
                            />
                            <span className={headerStyles.contactText}>
                                <a
                                    href="tel:+919503692077"
                                    aria-label="Call +91 95036 92077"
                                >
                                    +919503692077
                                </a>
                            </span>
                        </div>
                    </nav>

                    <button
                        className={headerStyles.mobileMenuButton}
                        onClick={handleMenuToggle}
                        onKeyDown={handleKeyDown}
                        aria-expanded={isMenuOpen}
                        aria-controls="mobile-menu"
                        aria-label={
                            isMenuOpen
                                ? "Close navigation menu"
                                : "Open navigation menu"
                        }
                        aria-haspopup="true"
                    >
                        <SafeIcon
                            icon={isMenuOpen ? FiX : FiMenu}
                            className={headerStyles.menuIcon}
                            aria-hidden="true"
                        />
                    </button>
                </div>

                {isMenuOpen && (
                    <nav
                        id="mobile-menu"
                        className={headerStyles.mobileMenu}
                        role="navigation"
                        aria-label="Mobile navigation"
                        aria-hidden={!isMenuOpen}
                    >
                        <div className={headerStyles.mobileMenuContent}>
                            <div
                                className={headerStyles.mobileContactItem}
                                role="group"
                                aria-label="Email contact"
                            >
                                <SafeIcon
                                    icon={FiMail}
                                    className={headerStyles.icon}
                                    aria-hidden="true"
                                />
                                <span className={headerStyles.contactText}>
                                    <a
                                        href="mailto:clubmangofriends@divineharmony.co.in"
                                        aria-label="Send email to clubmangofriends@divineharmony.co.in"
                                    >
                                        clubmangofriends@divineharmony.co.in
                                    </a>
                                </span>
                            </div>
                            <div
                                className={headerStyles.mobileContactItem}
                                role="group"
                                aria-label="Phone contact"
                            >
                                <SafeIcon
                                    icon={FiPhone}
                                    className={headerStyles.icon}
                                    aria-hidden="true"
                                />
                                <span className={headerStyles.contactText}>
                                    <a
                                        href="tel:+919503692077"
                                        aria-label="Call +91 95036 92077"
                                    >
                                        +919503692077
                                    </a>
                                </span>
                            </div>
                        </div>
                    </nav>
                )}
            </div>
        </header>
    );
};

export default Header;
